import React from 'react';
import {
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  Text,
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
  useSnackbar,
  Tooltip,
  AbstractButton,
  TextInput,
} from 'pounceV2';
import TablePlaceholderV3 from 'Components/TablePlaceholder/TablePlaceholderV3';
import FlatBadgeV3 from 'Components/badges/FlatBadge/FlatBadgeV3';

import {
  ListAIInferenceStreamsMetadata,
  ListAIInferenceStreamsMetadataDocument,
} from 'Components/AIExplainLogEvents/graphql/listAiInferenceStreamsMetadata.generated';
import { removeAIInferenceStreamFromCache } from 'Components/AIExplainLogEvents/utils/aiInferenceStreamCacheUtils';
import KendraSearchResults from 'Components/AIExplainLogEvents/KendraSearchResults';
import {
  useKendraSearch,
  getSearchLabel,
  SearchResultNode,
} from 'Components/AIExplainLogEvents/hooks/useKendraSearch';
import dayjs from 'dayjs';
import UserAvatar from 'Components/UserAvatar';
import { extractErrorMessage, getActorDisplayName } from 'Helpers/utils';
import useHover from 'Hooks/useHover';
import OptionsButtonV3 from 'Components/buttons/OptionsButton/OptionsButtonV3';
import { useEditAIInferenceStream } from 'Components/AIExplainLogEvents/graphql/editAiInferenceStream.generated';
import useUrlParams from 'Hooks/useUrlParams';
import { AISummaryStreamParams } from 'Components/AIExplainLogEvents/useFetchGenericAiSummaryV3';
import { AISummarizeKind } from 'Generated/schema';
import { useTheme } from '@mui/material';
import useModalV3 from 'Hooks/useModalV3';
import { ConfirmModalV3 } from 'Components/modals/ConfirmModal/ConfirmModalV3';
import EditableThreadNameV3, { getStreamName } from './EditableThreadNameV3';

const Thread = ({
  thread,
  kind,
  alias,
  onClose,
  onDelete,
}: {
  thread: ListAIInferenceStreamsMetadata['aiInferenceStreamsMetadata']['edges'][0]['node'];
  kind: AISummarizeKind;
  alias: string;
  onClose: () => void;
  onDelete: (id: string) => void;
}) => {
  const {
    palette: { componentTokens, tokens },
  } = useTheme();
  const {
    updateUrlParams,
    urlParams: { aiStream },
  } = useUrlParams<AISummaryStreamParams>();
  const { pushSnackbar } = useSnackbar();
  const displayName = React.useMemo(
    () => getActorDisplayName(thread.createdBy),
    [thread.createdBy],
  );
  const [isEditingName, setIsEditingName] = React.useState(false);
  const { showModal } = useModalV3();
  const { handlers, isHovering } = useHover();
  const [editAIInferenceStream] = useEditAIInferenceStream();

  const handlePinStream = React.useCallback(() => {
    editAIInferenceStream({
      variables: {
        input: { streamId: thread.streamId, pinned: !thread.pinned },
      },
      refetchQueries: [
        {
          query: ListAIInferenceStreamsMetadataDocument,
          variables: { input: { alias } },
        },
      ],
      onCompleted: () => {
        pushSnackbar({
          variant: 'success',
          title: thread.pinned ? 'Response unpinned' : 'Response pinned',
        });
      },
      onError: error => {
        pushSnackbar({
          variant: 'error',
          title: 'Failed to pin response',
          description: extractErrorMessage(error),
        });
      },
    });
  }, [editAIInferenceStream, pushSnackbar, thread, alias]);

  const handleDeleteStream = React.useCallback(() => {
    onDelete(thread.streamId);
    editAIInferenceStream({
      variables: { input: { streamId: thread.streamId, deleted: true } },
      update: cache => {
        removeAIInferenceStreamFromCache(cache, thread.streamId);
      },
      onCompleted: () => {
        pushSnackbar({
          variant: 'success',
          title: 'Response deleted',
        });
      },
      onError: error => {
        pushSnackbar({
          variant: 'error',
          title: 'Failed to delete response',
          description: extractErrorMessage(error),
        });
      },
    });
  }, [onDelete, thread, editAIInferenceStream, pushSnackbar]);

  const handleDeleteClick = React.useCallback(() => {
    showModal(
      <ConfirmModalV3
        variant="destructive"
        subtitle="Are you sure you want to permanently delete this response? This action cannot be undone."
        onConfirm={handleDeleteStream}
      />,
      { title: 'Confirm Delete' },
    );
  }, [showModal, handleDeleteStream]);

  const isActive = isHovering || isEditingName;
  const isSelected = thread.streamId === aiStream;

  return (
    <Box
      position="relative"
      {...handlers}
      width="100%"
      backgroundColor={
        isActive || isSelected
          ? componentTokens.selectMenuItems.color.item.background.row.hover
          : 'transparent'
      }
      borderLeft={`4px solid ${isSelected ? tokens.stroke.accent[1].base : 'transparent'}`}
    >
      <Grid templateColumns="4fr 2fr 3fr 2fr" gap={2} fontSize="medium" alignItems="center">
        <Flex alignItems="center" gap={2} truncated pl={5}>
          {thread.pinned && (
            <Icon type="pin-filled" size="small" mr={1} color={tokens.stroke.accent[1].base} />
          )}
          {!thread.private && <FlatBadgeV3 key={thread.streamId}>Shared</FlatBadgeV3>}
          <Flex alignItems="center" textAlign="left" truncated>
            {isEditingName ? (
              <Box mt="1px" mb="5px">
                <EditableThreadNameV3
                  stream={thread}
                  kind={kind}
                  onCancelEdit={() => setIsEditingName(false)}
                  size="small"
                />
              </Box>
            ) : (
              <AbstractButton
                py={3}
                color={componentTokens.selectMenuItems.color.item.text.row.resting}
                _hover={{
                  color: componentTokens.selectMenuItems.color.item.text.row.hover,
                }}
                pr={2}
                truncated
                onClick={() => {
                  updateUrlParams({ aiStream: thread.streamId });
                  onClose();
                }}
              >
                {getStreamName(thread, kind)}
              </AbstractButton>
            )}
          </Flex>
        </Flex>
        <Flex truncated align="center" gap={2}>
          {thread.createdBy && (
            <>
              <UserAvatar name={displayName} entityId={thread.createdBy.id} />
              <Text truncated>{displayName}</Text>
            </>
          )}
        </Flex>
        <Flex alignItems="center" gap={1} truncated>
          {thread.userPrompt && (
            <>
              <Icon
                type="user"
                size="small"
                color={tokens.content.text.accent[14][2]}
                flexShrink={0}
              />
              <Tooltip
                content={thread.userPrompt.length > 64 ? thread.userPrompt : ''}
                disableHoverListener={thread.userPrompt.length <= 64}
              >
                <Text color={tokens.content.text.accent[14][2]} truncated>
                  {`"${
                    thread.userPrompt.length > 64
                      ? `${thread.userPrompt.slice(0, 64)}...`
                      : thread.userPrompt
                  }"`}
                </Text>
              </Tooltip>
            </>
          )}
        </Flex>
        <Tooltip content={dayjs(thread.createdAt).format('MMMM D, YYYY h:mm A')} placement="left">
          <Text textAlign="right" pr={15} truncated>
            {dayjs(thread.createdAt).fromNow()}
          </Text>
        </Tooltip>
      </Grid>
      {isActive && (
        <Box position="absolute" right={24} top="calc(50% - 15px)">
          <Dropdown>
            <Tooltip content="More actions" placement="left">
              <DropdownButton
                as={OptionsButtonV3}
                aria-label="Toggle Response Options"
                icon="options-dots-vertical"
                size="small"
                variant="outline"
              />
            </Tooltip>
            <DropdownMenu>
              <Tooltip content="Change the display name of this response" placement="left">
                <DropdownItem
                  onSelect={() => {
                    setIsEditingName(true);
                  }}
                >
                  Rename response
                </DropdownItem>
              </Tooltip>
              <Tooltip
                content={
                  thread.pinned
                    ? 'Remove this response from the top of the list'
                    : 'Keep this response at the top of the list'
                }
                placement="left"
              >
                <DropdownItem onSelect={handlePinStream}>
                  {thread.pinned ? 'Unpin' : 'Pin as default'}
                </DropdownItem>
              </Tooltip>
              <Tooltip content="Permanently delete this response" placement="left">
                <DropdownItem onSelect={handleDeleteClick}>Delete response</DropdownItem>
              </Tooltip>
            </DropdownMenu>
          </Dropdown>
        </Box>
      )}
    </Box>
  );
};

const MemoizedThread = React.memo(Thread);

const ThreadGroup = ({
  title,
  threads,
  kind,
  alias,
  onClose,
  onDelete,
}: {
  title?: string;
  threads: Array<ListAIInferenceStreamsMetadata['aiInferenceStreamsMetadata']['edges'][0]['node']>;
  kind: AISummarizeKind;
  alias: string;
  onClose: () => void;
  onDelete: (id: string) => void;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  if (!threads?.length) {
    return null;
  }

  return (
    <Box>
      {title && (
        <Text
          mb={2}
          px={6}
          color={tokens.content.text.accent[14][2]}
          fontWeight="bold"
          fontSize="small"
        >
          {title}
        </Text>
      )}
      {threads.map(thread => (
        <MemoizedThread
          key={thread.streamId}
          thread={thread}
          kind={kind}
          alias={alias}
          onClose={onClose}
          onDelete={onDelete}
        />
      ))}
    </Box>
  );
};

const ThreadListV3 = ({
  isOpen,
  kind,
  alias,
  onClose,
  streamsMetadata,
  hasNextPage,
  sentinelRef,
  initialSearchQuery,
  topOffset = 80,
}: {
  isOpen: boolean;
  kind: AISummarizeKind;
  alias: string;
  onClose: () => void;
  streamsMetadata:
    | Array<ListAIInferenceStreamsMetadata['aiInferenceStreamsMetadata']['edges'][0]['node']>
    | undefined;
  loading: boolean;
  hasNextPage?: boolean;
  sentinelRef?: React.RefObject<HTMLDivElement>;
  initialSearchQuery?: string | null;
  topOffset?: number;
}) => {
  const {
    palette: { tokens },
  } = useTheme();
  const { updateUrlParams, urlParams } = useUrlParams<AISummaryStreamParams>();

  const {
    searchQuery,
    setSearchQuery,
    searchAll,
    setSearchAll,
    searchMine,
    setSearchMine,
    showSearchResults,
    hasSearched,
    allSearchResults,
    handleSearch,
    handleInitialSearch,
    handleClearResults,
    handleLoadMore,
    searchLoading,
    searchError,
    isKendraEnabled,
    totalCount,
    searchHasNextPage,
  } = useKendraSearch({ isConversation: false, alias });

  // Track the last processed initial query to avoid re-processing
  const [lastProcessedQuery, setLastProcessedQuery] = React.useState<string | null>(null);

  // Handle initial search query when it's provided
  React.useEffect(() => {
    if (initialSearchQuery && isOpen && initialSearchQuery !== lastProcessedQuery) {
      setLastProcessedQuery(initialSearchQuery);
      handleInitialSearch(initialSearchQuery);
    }
  }, [initialSearchQuery, isOpen, lastProcessedQuery, handleInitialSearch]);

  const filteredThreads = React.useMemo(() => {
    const threads = streamsMetadata;
    const pinnedThreads = threads?.filter(thread => thread.pinned);
    const unpinnedThreads = threads?.filter(thread => !thread.pinned);
    const todayThreads = unpinnedThreads?.filter(thread => {
      const threadDate = dayjs(thread.createdAt);
      const today = dayjs();
      return threadDate.isSame(today, 'day');
    });
    const restThreads = unpinnedThreads?.filter(thread => !todayThreads?.includes(thread));

    return { pinnedThreads, todayThreads, restThreads };
  }, [streamsMetadata]);

  const handleDelete = React.useCallback(
    (id: string) => {
      // Filter out the thread being deleted to get the accurate remaining count
      const remainingThreads = streamsMetadata.filter(thread => thread.streamId !== id);

      if (id === urlParams.aiStream) {
        if (remainingThreads.length) {
          updateUrlParams({ aiStream: remainingThreads[0].streamId });
        } else {
          // No other threads remain, close the panel
          updateUrlParams({ aiStream: undefined });
        }
      } else if (remainingThreads.length === 0) {
        // This was the last thread, close the panel
        updateUrlParams({ aiStream: undefined });
      }
    },
    [streamsMetadata, updateUrlParams, urlParams.aiStream],
  );

  const handleSearchResultClick = React.useCallback(
    (node: SearchResultNode) => {
      updateUrlParams({ aiStream: node.streamId });
      onClose();
    },
    [updateUrlParams, onClose],
  );

  const { pinnedThreads, todayThreads, restThreads } = filteredThreads;

  return (
    <Flex
      direction="column"
      overflow="auto"
      gap={5}
      zIndex={1}
      position="absolute"
      backgroundColor={tokens.background.neutral.canvas.base}
      top={topOffset}
      left={0}
      right={0}
      bottom={0}
      py={6}
      transition="transform 0.3s ease-in-out"
      transform={isOpen ? 'translateY(0)' : 'translateY(-105%)'}
      visibility={isOpen ? 'visible' : 'hidden'}
    >
      {isKendraEnabled && (
        <Box px={6} pb={4}>
          <Box>
            <TextInput
              label={getSearchLabel(searchAll, searchMine, alias, false)}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  handleSearch(searchQuery);
                }
              }}
              placeholder="Enter search terms..."
              icon="search"
              iconAlignment="left"
              style={{ width: '100%' }}
              autoFocus
            />
            <Flex alignItems="center" gap={3} mt={2}>
              <Button
                size="small"
                variant={searchMine ? 'primary' : 'outline'}
                onClick={() => {
                  const newSearchMine = !searchMine;
                  setSearchMine(newSearchMine);
                  if (searchQuery.trim() && showSearchResults) {
                    handleSearch(searchQuery, { searchMineOverride: newSearchMine });
                  }
                }}
                aria-label="Toggle search mine filter"
              >
                Search only mine
              </Button>
              <Button
                size="small"
                variant={searchAll ? 'primary' : 'outline'}
                onClick={() => {
                  const newSearchAll = !searchAll;
                  setSearchAll(newSearchAll);
                  if (searchQuery.trim() && showSearchResults) {
                    handleSearch(searchQuery, { searchAllOverride: newSearchAll });
                  }
                }}
                aria-label="Toggle search all responses filter"
              >
                Search all responses
              </Button>
            </Flex>
          </Box>
          {searchLoading && allSearchResults.length === 0 && (
            <Box py={2}>
              <TablePlaceholderV3 rowCount={2} />
            </Box>
          )}
          {searchError && (
            <Box py={2}>
              <Text color={tokens.content.text.semantic.error.base} variant="body/sm">
                Search failed: {searchError.message || 'Unknown error'}
              </Text>
            </Box>
          )}
          {showSearchResults && allSearchResults.length > 0 && (
            <Box pt="5px">
              <KendraSearchResults
                results={allSearchResults}
                totalCount={totalCount}
                hasNextPage={searchHasNextPage}
                loading={searchLoading}
                onResultClick={handleSearchResultClick}
                onClearResults={handleClearResults}
                onLoadMore={handleLoadMore}
                showAliasType
              />
            </Box>
          )}
          {showSearchResults && hasSearched && allSearchResults.length === 0 && !searchLoading && (
            <Box py={4} textAlign="center">
              <Text color={tokens.content.text.accent[14][2]}>
                No responses found that match your filter criteria.
              </Text>
              <Text color={tokens.content.text.accent[14][3]} mt={2}>
                Try different filters?
              </Text>
            </Box>
          )}
        </Box>
      )}
      <ThreadGroup
        threads={pinnedThreads}
        kind={kind}
        alias={alias}
        onClose={onClose}
        onDelete={handleDelete}
      />
      <ThreadGroup
        title="TODAY"
        threads={todayThreads}
        kind={kind}
        alias={alias}
        onClose={onClose}
        onDelete={handleDelete}
      />
      <ThreadGroup
        title="LAST 30 DAYS"
        threads={restThreads}
        kind={kind}
        alias={alias}
        onClose={onClose}
        onDelete={handleDelete}
      />
      {hasNextPage && (
        <Box py={4} ref={sentinelRef} data-testid="pagination-loading">
          <TablePlaceholderV3 rowCount={3} />
        </Box>
      )}
    </Flex>
  );
};

export default React.memo(ThreadListV3);
